<template lang="html">
  <div class="chart">
    <img
      v-if="chartBase64"
      class="chart-image"
      :src="chartBase64" />
    <canvas
      ref="chart"
      @click="getPoint($event)"
      v-show="!image"
      :width="width"
      :height="height">
    </canvas>
  </div>
</template>

<script>
/**
* Base component for charts, using Chart.js
*/

import merge from 'deepmerge'
import { arrayMerge } from '@/utils/arrays'
import Chart from 'chart.js'
import 'chartjs-plugin-datalabels' // ChartDataLabels from 'chartjs-plugin-datalabels'

Chart.defaults.global.defaultFontColor = '#000000'
// Chart.defaults.global.defaultFontFamily = 'OpenSans'

export default {
  name: 'Chart',
  data () {
    return {
      chartCanvas: null,
      animationComplete: false
    }
  },
  props: {
    /**
     * Chart type: 'Doughnut', 'Line'
     */
    type: {
      type: String,
      required: true
    },
    /**
     * Datasets formatted for Chart.js
     */
    datasets: {
      type: Object,
      required: true
    },
    /**
     * Overwrite and merged with default options
     */
    options: {
      type: Object
    },
    /**
     * Canvas width (responsive so used as a ratio)
     */
    width: {
      type: Number,
      default: 400
    },
    /**
     * Canvas height (responsive so used as a ratio)
     */
    height: {
      type: Number,
      default: 400
    },
    /**
     * If true, chart is displayed as a png, not a canvas (useful for pdf)
     */
    image: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    datasets (val, old) {
      if (this.chartCanvas && this.chartCanvas.data) {
        this.chartCanvas.data.datasets = val.datasets
        this.chartCanvas.data.labels = val.labels
        this.chartCanvas.update()
      }
    },
    lang () {
      this.chartCanvas.update()
    }
  },
  computed: {
    genericOptions () {
      const opts = {
        legend: {
          onHover: (event) => {
            event.target.style.cursor = 'pointer'
          },
          onClick: (event, legendItem) => {
            const value = legendItem.text
            /**
             * Returns { type: 'data', label, value, dataset } or { type: 'dataset', dataset }
             *
             * @event click
             * @type {object}
             */
            this.$emit('click', { type: 'dataset', value })
          },
          position: 'bottom',
          labels: {
            fontColor: '#7D7769'
          }
        },
        tooltips: {
          backgroundColor: '#000000',
          displayColors: true
        },
        hover: {
          onHover (event) {
            const point = this.getElementAtEvent(event)
            if (point.length) event.target.style.cursor = 'pointer'
            else event.target.style.cursor = 'default'
          }
        },
        animation: {
          onComplete: (animation) => {
            if (this.image) this.setAnimationComplete()
            /**
             * Emited when animations are complete, useful to know when png version is available in the DOM
             *
             * @event ready
             * @type {event}
             */
            this.$emit('ready')
          }
        }
      }
      if (this.image) opts.animation.duration = 0
      return this.options ? merge(opts, this.options, { arrayMerge }) : opts
    },
    chartBase64 () {
      return this.chartCanvas && this.image && this.animationComplete ? this.chartCanvas.toBase64Image() : null
    }
  },
  methods: {
    renderChart (data) {
      this.chartCanvas = new Chart(this.$refs.chart.getContext('2d'), {
        defaultFontColor: '#000000',
        defaultFontFamily: 'OpenSans',
        type: this.type,
        data: data,
        options: this.genericOptions
      })
      this.$refs.chart.onclick = e => {
        const dataClicked = this.chartCanvas.getElementAtEvent(e)[0]
        if (dataClicked) {
          const label = this.chartCanvas.data.labels[dataClicked._index]
          const dataset = this.chartCanvas.data.datasets[dataClicked._datasetIndex]
          const value = dataset.data[dataClicked._index]
          this.$emit('click', { type: 'data', label, value, dataset })
        }
      }
    },
    setAnimationComplete () {
      this.animationComplete = true
    },
    getPoint (e) {
      const activePoint = this.chartCanvas.chart.tooltip._active[0]
      if (activePoint) {
        const index = activePoint._index
        const datasetIndex = activePoint._datasetIndex
        const label = this.datasets.labels[index]
        const dataset = this.datasets.datasets[datasetIndex].label
        const data = this.datasets.datasets[datasetIndex].data[index]
        this.$emit('clickchart', { label, dataset, data })
      }
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.renderChart(this.datasets)
    })
  }
}
</script>

<style lang="scss">
.chart {
  display: block;
  position: relative;

  .chart-image {
    display: block;
    position: relative;
    width: 100%;
    height: auto;
  }

}
</style>
